/**
 * 航跡（FixSample[]）を、ブリッジ→ブラウザの転送向けに小さくする形式。
 *
 * ★ なぜ要るか（2026-10-09、利用者から「接続を押してから航跡が出るまで
 *   7機で7秒ほどかかる」と指摘）
 *   接続直後のスナップショットは全機の全点を FixSample のJSONそのままで
 *   送っていた。1点あたり約290バイト（項目名・null・小数の桁がそのまま
 *   載る）で、7機×1時間（1秒1点）で約7.3MB。SSEは圧縮も掛からない
 *   （`Cache-Control: no-transform`）ため、スマホの回線ではこの受信だけで
 *   数秒かかっていた。
 *
 * 形式（列指向・差分・整数化）:
 * - 端末ごとに、項目ごとの配列を持つ（項目名を1点ずつ繰り返さない）
 * - 時刻・緯度・経度・高度は直前の点との差分の整数（航跡は連続しているので
 *   差分は小さく、JSONの桁数が減る）
 * - 速度・方位などは差分にせず、単位を決めた整数にする
 * - 全点がnullの項目は列ごと省く（null）。全点が同じ文字列の項目
 *   （timeQuality・source）は1つの値で表す
 *
 * 丸めの単位（実用上の精度を落とさない範囲）:
 * - 緯度・経度: 1e-7度（約1cm）。NMEAの分の小数4桁（約0.2m）より細かい
 * - 高度・精度: 0.1m
 * - 速度・昇降率: 0.01m/s
 * - 方位: 0.1度
 * - 時刻: 1ms（丸めない）
 */

import type { FixSample, SourceKind } from './types.ts';

export interface PackedTrack {
  /** 形式の版。変えるときは上げて、古い版を読めるようにしておく */
  v: 1;
  terminalId: string;
  n: number;
  /** 時刻 [ms]。先頭は絶対値、以降は直前との差分 */
  t: number[];
  /** 1e-7度単位。差分 */
  lat: number[];
  lon: number[];
  /** 0.1m単位。差分（nullの点はnull、差分は直前の非nullの値から取る） */
  altMsl: Array<number | null> | null;
  altPressure: Array<number | null> | null;
  /** 0.01m/s単位 */
  speed: Array<number | null> | null;
  /** 0.1度単位 */
  heading: Array<number | null> | null;
  /** 0.01m/s単位 */
  verticalSpeed: Array<number | null> | null;
  /** 0.1m単位 */
  accuracy: Array<number | null> | null;
  altAccuracy: Array<number | null> | null;
  /** 全点同じなら1つの値、違えば点ごとの配列 */
  timeQuality: FixSample['timeQuality'] | Array<FixSample['timeQuality']>;
  source: SourceKind | SourceKind[];
}

const LATLON_SCALE = 1e7;

function finite(v: number | null | undefined): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

/** 単位を決めて整数にした列。全点がnullならnull */
function scaledColumn(values: Array<number | null | undefined>, scale: number, delta: boolean): Array<number | null> | null {
  if (!values.some(finite)) return null;
  let prev = 0;
  return values.map((v) => {
    if (!finite(v)) return null;
    const q = Math.round(v * scale);
    if (!delta) return q;
    const d = q - prev;
    prev = q;
    return d;
  });
}

function unscaledColumn(column: Array<number | null> | null, n: number, scale: number, delta: boolean): Array<number | null> {
  if (!column) return new Array<number | null>(n).fill(null);
  let acc = 0;
  return column.map((q) => {
    if (q === null) return null;
    if (!delta) return q / scale;
    acc += q;
    return acc / scale;
  });
}

function uniformOrArray<T extends string>(values: T[]): T | T[] {
  return values.every((v) => v === values[0]) ? values[0] : values;
}

/** 1端末ぶんの航跡を詰める。fixesは全て`terminalId`の端末のもの */
export function packFixes(terminalId: string, fixes: FixSample[]): PackedTrack {
  const t: number[] = [];
  const lat: number[] = [];
  const lon: number[] = [];
  let prevT = 0;
  let prevLat = 0;
  let prevLon = 0;
  for (const f of fixes) {
    t.push(f.t - prevT);
    prevT = f.t;
    const qLat = Math.round(f.lat * LATLON_SCALE);
    const qLon = Math.round(f.lon * LATLON_SCALE);
    lat.push(qLat - prevLat);
    lon.push(qLon - prevLon);
    prevLat = qLat;
    prevLon = qLon;
  }
  return {
    v: 1,
    terminalId,
    n: fixes.length,
    t,
    lat,
    lon,
    altMsl: scaledColumn(fixes.map((f) => f.altMsl), 10, true),
    altPressure: scaledColumn(fixes.map((f) => f.altPressure), 10, true),
    speed: scaledColumn(fixes.map((f) => f.speed), 100, false),
    heading: scaledColumn(fixes.map((f) => f.heading), 10, false),
    verticalSpeed: scaledColumn(fixes.map((f) => f.verticalSpeed), 100, false),
    accuracy: scaledColumn(fixes.map((f) => f.accuracy), 10, false),
    altAccuracy: scaledColumn(fixes.map((f) => f.altAccuracy), 10, false),
    timeQuality: fixes.length > 0 ? uniformOrArray(fixes.map((f) => f.timeQuality)) : 'derived',
    source: fixes.length > 0 ? uniformOrArray(fixes.map((f) => f.source)) : 'balloon',
  };
}

/** `packFixes`の逆。値は丸めの単位の範囲で元に戻る */
export function unpackFixes(p: PackedTrack): FixSample[] {
  const n = p.n;
  const altMsl = unscaledColumn(p.altMsl, n, 10, true);
  const altPressure = unscaledColumn(p.altPressure, n, 10, true);
  const speed = unscaledColumn(p.speed, n, 100, false);
  const heading = unscaledColumn(p.heading, n, 10, false);
  const verticalSpeed = unscaledColumn(p.verticalSpeed, n, 100, false);
  const accuracy = unscaledColumn(p.accuracy, n, 10, false);
  const altAccuracy = unscaledColumn(p.altAccuracy, n, 10, false);
  const out: FixSample[] = new Array(n);
  let t = 0;
  let lat = 0;
  let lon = 0;
  for (let i = 0; i < n; i++) {
    t += p.t[i];
    lat += p.lat[i];
    lon += p.lon[i];
    out[i] = {
      terminalId: p.terminalId,
      t,
      timeQuality: typeof p.timeQuality === 'string' ? p.timeQuality : p.timeQuality[i],
      lat: lat / LATLON_SCALE,
      lon: lon / LATLON_SCALE,
      altMsl: altMsl[i],
      altPressure: altPressure[i],
      speed: speed[i],
      heading: heading[i],
      verticalSpeed: verticalSpeed[i],
      accuracy: accuracy[i],
      altAccuracy: altAccuracy[i],
      source: typeof p.source === 'string' ? p.source : p.source[i],
    };
  }
  return out;
}
